import { Box, Flex, Tooltip } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MantineReactTable,
  useMantineReactTable,
} from 'mantine-react-table';
import { TextComponent } from '@manifest-cyber/design-system/lib/components/TextComponent';
import { useFetchModels } from '../../hooks/queries/useFetchModels';
import { Model } from '../../api/models.api';
import { getDefaultTableOptions } from '../../components/MRT/ManifestMRT';
import DataTableFooter from '../../components/DataTables/DataTableFooter';
import Icon from '../../components/Icon';
import styles from './ModelsTable.module.scss';

export const renderText = (text: string = '') => {
  return (
    <Tooltip disabled={(text?.length ?? 0) <= 30} label={text}>
      <Box className={styles['cell-content-container']}>
        <TextComponent lineClamp={2} className={styles['truncated-text']}>
          {text}
        </TextComponent>
      </Box>
    </Tooltip>
  );
};

const defaultTableOptions = getDefaultTableOptions<Model>();

interface Props {
  assetId?: string;
}

export const ModelsTable = ({ assetId }: Props) => {
  const { t } = useTranslation();

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });

  const {
    data: modelsResponse,
    isLoading,
    isFetching,
  } = useFetchModels(
    {
      assetId: assetId || '',
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
    },
    {
      enabled: !!assetId,
    },
  );

  const models = modelsResponse?.data || [];
  const totalModels = modelsResponse?.total || 0;

  const columns = useMemo<MRT_ColumnDef<Model>[]>(
    () => [
      {
        accessorFn: (row) => row.name,
        id: 'name',
        header: t('page.asset.models.name'),
        minSize: 200,
        Cell: ({ row }) => {
          if (row.original?.homepageUrl) {
            return (
              <a
                href={row.original.homepageUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {renderText(row.original.name || '')}
              </a>
            );
          }
          return renderText(row.original?.name || '');
        },
      },
      {
        accessorFn: (row) => row.version,
        id: 'version',
        header: t('page.asset.models.version'),
        Cell: ({ row }) => {
          const version = row.original.version;

          return version ? (
            renderText(version)
          ) : (
            <span className={styles.cell}>{t('global.notProvided')}</span>
          );
        },
      },
      {
        accessorFn: (row) => row.supplier,
        id: 'supplier',
        header: t('page.asset.models.supplier'),
        Cell: ({ row }) => {
          return row.original.supplier ? (
            renderText(row.original.supplier)
          ) : (
            <span className={styles.cell}>{t('global.notProvided')}</span>
          );
        },
      },
      {
        accessorFn: (row) => row.licenses,
        id: 'licenses',
        header: t('page.asset.models.licenses'),
        enableSorting: false,
        Cell: ({ row }) => {
          if (row.original.licenses && row.original.licenses.length > 0) {
            const licenseText = row.original.licenses
              .map((license: any) => license.rawLicenseId || license.licenseId)
              .filter(Boolean)
              .join(', ');
            return renderText(licenseText);
          }
          return <span className={styles.cell}>{t('global.notProvided')}</span>;
        },
      },
      {
        accessorFn: (row) => row.dateCreated,
        id: 'dateCreated',
        header: t('page.asset.models.created'),
        Cell: ({ row }) => {
          return row.original.dateCreated ? (
            renderText(
              DateTime.fromISO(row.original.dateCreated).toLocaleString(
                DateTime.DATETIME_MED,
              ),
            )
          ) : (
            <span className={styles.cell}>{t('global.notProvided')}</span>
          );
        },
      },
    ],
    [t],
  );

  const table = useMantineReactTable<Model>({
    ...defaultTableOptions,
    mantinePaperProps: {
      className: 'manifest-data-table-no-footer',
    },
    columns,
    data: models,
    enableFilters: false,
    enablePagination: false, // Disable pagination for this table
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    rowCount: totalModels,
    initialState: {
      ...defaultTableOptions.initialState,
      showGlobalFilter: false,
    },
    state: {
      pagination,
      isLoading: isLoading,
      showLoadingOverlay: isFetching,
    },
    onPaginationChange: setPagination,
    enableGlobalFilter: false,
    enableTopToolbar: false,
    renderEmptyRowsFallback: () => (
      <Flex
        direction="column"
        justify="center"
        align="center"
        h={200}
        bg="var(--color-background-muted)"
        style={{ borderRadius: 8 }}
      >
        <Icon icon="robot" size="2x" style={{ marginBottom: 16, opacity: 0.7 }} />
        <TextComponent>{t('page.asset.models.no-models')}</TextComponent>
      </Flex>
    ),
  });

  return (
    <div className="list-components">
      <MantineReactTable table={table} />
      <DataTableFooter
        currentPage={pagination.pageIndex}
        limitPerPage={pagination.pageSize}
        totalResults={totalModels}
        onChangePage={setPagination}
      />
    </div>
  );
};
